import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

type AppRole = "admin" | "technician";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: AppRole | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  role: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const checkActiveAndFetchRole = async (userId: string): Promise<boolean> => {
    const { data: profile } = await supabase
      .from("profiles")
      .select("is_active")
      .eq("user_id", userId)
      .maybeSingle();

    if (profile && profile.is_active === false) {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setRole(null);
      return false;
    }

    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .maybeSingle();
    setRole((data?.role as AppRole) ?? null);
    return true;
  };

  useEffect(() => {
    let isMounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!isMounted) return;
        
        if (_event === "PASSWORD_RECOVERY") {
          navigate("/reset-password");
        }

        if (session?.user) {
          setSession(session);
          setUser(session.user);
          setLoading(false);
          
          // Fetch role in background with setTimeout to avoid Supabase auth deadlock
          setTimeout(async () => {
            if (!isMounted) return;
            try {
              const isActive = await checkActiveAndFetchRole(session.user.id);
              if (!isActive && isMounted) {
                setSession(null);
                setUser(null);
              }
            } catch (error) {
              console.error("Auth role check error:", error);
            }
          }, 0);
        } else {
          setSession(null);
          setUser(null);
          setRole(null);
          setLoading(false);
        }
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRole(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, role, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
